import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getConversation,
  getConversations,
  getUnreadCount,
  markRead,
  sendMessage,
  startConversation,
  uploadAttachment,
} from './index';
import { withPrincipal } from 'src/api/principals';

const K_CONVERSATIONS = 'K_CONVERSATIONS';
const K_CONVERSATION = 'K_CONVERSATION';
const K_UNREAD_COUNT = 'K_UNREAD_COUNT';

export const useConversations = (
  propertyId: string,
  opts?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: withPrincipal([K_CONVERSATIONS, propertyId]),
    queryFn: () => getConversations({ propertyId }),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    enabled: !!propertyId && (opts?.enabled ?? true),
  });

export const useConversation = (propertyId: string, id: string) =>
  useQuery({
    queryKey: withPrincipal([K_CONVERSATION, propertyId, id]),
    queryFn: () => getConversation({ propertyId, id }),
    staleTime: 30 * 1000,
    enabled: !!propertyId && !!id,
  });

// Drives the sidebar nav unread badge (PortalSidebar).
export const useUnreadCount = (
  propertyId: string,
  opts?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: withPrincipal([K_UNREAD_COUNT, propertyId]),
    queryFn: () => getUnreadCount({ propertyId }),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    enabled: !!propertyId && (opts?.enabled ?? true),
  });

export const useStartConversation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: startConversation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: withPrincipal([K_CONVERSATIONS]) });
      qc.invalidateQueries({ queryKey: withPrincipal([K_UNREAD_COUNT]) });
    },
  });
};

export const useSendMessage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: sendMessage,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: withPrincipal([K_CONVERSATION]) });
      qc.invalidateQueries({ queryKey: withPrincipal([K_CONVERSATIONS]) });
      qc.invalidateQueries({ queryKey: withPrincipal([K_UNREAD_COUNT]) });
    },
  });
};

export const useMarkRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: withPrincipal([K_CONVERSATION]) });
      qc.invalidateQueries({ queryKey: withPrincipal([K_CONVERSATIONS]) });
      qc.invalidateQueries({ queryKey: withPrincipal([K_UNREAD_COUNT]) });
    },
  });
};

export const useUploadAttachment = () =>
  useMutation({
    mutationFn: uploadAttachment,
  });
